export interface SpatialNode {
  expressID: number;
  type: string;
  children: SpatialNode[];
}

export type TreeNodeKind = 'spatial' | 'typeGroup' | 'element';

export interface ModelTreeNode {
  key: string;
  expressID: number | null;
  label: string;
  typeName: string;
  kind: TreeNodeKind;
  /** Express IDs of all leaf elements under this node (for visibility ops). */
  elementIDs: number[];
  children: ModelTreeNode[];
}

// web-ifc reports the project as "IFCPROJECT" but nested nodes CamelCase,
// so spatial detection must be case-insensitive.
const SPATIAL_TYPES = new Set([
  'ifcproject',
  'ifcsite',
  'ifcbuilding',
  'ifcbuildingstorey',
  'ifcspace',
]);

function isSpatial(node: SpatialNode): boolean {
  return SPATIAL_TYPES.has(node.type.toLowerCase());
}

function stripIfc(typeName: string): string {
  return typeName.replace(/^ifc/i, '');
}

const naturalCompare = (a: string, b: string): number =>
  a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' });

export function buildModelTree(
  spatial: SpatialNode,
  getName: (expressID: number) => string | null,
): ModelTreeNode {
  return buildSpatialNode(spatial, getName);
}

function buildSpatialNode(
  node: SpatialNode,
  getName: (expressID: number) => string | null,
): ModelTreeNode {
  const spatialChildren = node.children.filter(isSpatial);
  const elementChildren = node.children.filter((c) => !isSpatial(c));

  const childNodes = spatialChildren.map((c) => buildSpatialNode(c, getName));

  const byType = new Map<string, SpatialNode[]>();
  for (const el of elementChildren) {
    const list = byType.get(el.type) ?? [];
    list.push(el);
    byType.set(el.type, list);
  }
  const typeGroups = [...byType.entries()]
    .sort(([a], [b]) => naturalCompare(a, b))
    .map(([typeName, elements]) => buildTypeGroup(node.expressID, typeName, elements, getName));

  const children = [...childNodes, ...typeGroups];
  const elementIDs = children.flatMap((c) => c.elementIDs);

  return {
    key: `s${node.expressID}`,
    expressID: node.expressID,
    label: getName(node.expressID) ?? `${stripIfc(node.type)} #${node.expressID}`,
    typeName: node.type,
    kind: 'spatial',
    elementIDs,
    children,
  };
}

function buildTypeGroup(
  parentID: number,
  typeName: string,
  elements: SpatialNode[],
  getName: (expressID: number) => string | null,
): ModelTreeNode {
  const children = elements
    .map((el): ModelTreeNode => {
      // element assemblies can nest; flatten their descendants into the ids
      const descendantIDs = collectElementIDs(el);
      return {
        key: `e${el.expressID}`,
        expressID: el.expressID,
        label: getName(el.expressID) ?? `${stripIfc(el.type)} #${el.expressID}`,
        typeName: el.type,
        kind: 'element',
        elementIDs: descendantIDs,
        children: [],
      };
    })
    .sort((a, b) => naturalCompare(a.label, b.label));

  return {
    key: `t${parentID}:${typeName}`,
    expressID: null,
    label: stripIfc(typeName),
    typeName,
    kind: 'typeGroup',
    elementIDs: children.flatMap((c) => c.elementIDs),
    children,
  };
}

function collectElementIDs(node: SpatialNode): number[] {
  return [node.expressID, ...node.children.flatMap(collectElementIDs)];
}

/**
 * Returns a copy of the tree without the deleted elements: element rows for
 * deleted ids disappear, type groups that lose every child disappear with
 * them, and spatial containers stay so the building structure remains
 * navigable even when emptied. Returns the input untouched when nothing is
 * deleted, so the common case costs nothing.
 */
export function pruneDeleted(
  node: ModelTreeNode,
  deletedIDs: ReadonlySet<number>,
): ModelTreeNode | null {
  if (deletedIDs.size === 0) return node;
  if (node.kind === 'element' && node.expressID !== null && deletedIDs.has(node.expressID)) {
    return null;
  }

  const children = node.children
    .map((child) => pruneDeleted(child, deletedIDs))
    .filter((child): child is ModelTreeNode => child !== null);

  if (node.kind === 'typeGroup' && children.length === 0) return null;

  const elementIDs = node.elementIDs.filter((id) => !deletedIDs.has(id));
  return { ...node, elementIDs, children };
}

/** Human-readable label for an element, for messages that name what changed. */
export function findNodeLabel(node: ModelTreeNode | null, expressID: number): string | null {
  if (!node) return null;
  if (node.kind === 'element' && node.expressID === expressID) return node.label;
  for (const child of node.children) {
    const found = findNodeLabel(child, expressID);
    if (found !== null) return found;
  }
  return null;
}
